import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType,
  Header, Footer, PageNumber
} from 'docx'
import { saveAs } from 'file-saver'

// Executive Advisory Report DOCX exporter.
//
// POC scope: Cover + TOC + Executive Summary only (matches the PDF POC).
// The full 23-page template rebuild will extend this with Engagement Findings,
// Compliance & Maturity, Key Strengths/Risks, Strategic Opportunities,
// Cyber Insurance, Strategic Next Steps + Roadmap, In Summary, and Appendices.

const DEFAULT_BRAND = {
  firmName: 'Sidewalk Security Advisors',
  logoUrl: null,
  primaryHex: '0D1B2A',
  accentHex: 'C9A84C',
  trademarkLine: null,
  firmAddress: '200 Missionary Rdg STE 319, Birmingham, AL 35242',
  authorName: 'Jeremy Krienke',
  authorTitle: 'Principal Advisor',
  firmLegalName: 'Sidewalk Security Advisors LLC',
  font: 'Aptos'
}

const CW = 10080  // content width in DXA (twips) for 8.5" page minus margins
const SP6 = 120

export async function exportAdvisoryReportDocx({
  client,
  context,
  draft,
  review,
  brand = DEFAULT_BRAND
}) {
  const b = { ...DEFAULT_BRAND, ...brand }
  const P = strip(b.primaryHex || DEFAULT_BRAND.primaryHex)
  const A = strip(b.accentHex || DEFAULT_BRAND.accentHex)
  const F = b.font || DEFAULT_BRAND.font
  const firm = b.firmName || DEFAULT_BRAND.firmName
  const clientName = client?.name || 'Client Organization'
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const txt = (t, o = {}) => new TextRun({ text: String(t ?? ''), font: F, size: 22, color: '2D3748', ...o })

  const coverBlock = buildCover({ txt, firm, clientName, dateStr, P, A, b })
  const tocBlock = buildToc({ txt, P, A, draft })
  const execSummaryBlock = buildExecutiveSummary({ txt, P, A, draft, review, context })

  const doc = new Document({
    styles: { default: { document: { run: { font: F, size: 22 } } } },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 }
        }
      },
      headers: {
        default: new Header({
          children: [
            new Table({
              width: { size: CW, type: WidthType.DXA },
              columnWidths: [Math.floor(CW * 0.6), Math.floor(CW * 0.4)],
              borders: noBd(),
              rows: [new TableRow({ children: [
                new TableCell({
                  width: { size: Math.floor(CW * 0.6), type: WidthType.DXA },
                  borders: noBd(),
                  children: [new Paragraph({
                    children: [new TextRun({ text: b.firmAddress || '', font: F, size: 14, color: '64748B' })]
                  })]
                }),
                new TableCell({
                  width: { size: Math.floor(CW * 0.4), type: WidthType.DXA },
                  borders: noBd(),
                  children: [new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                      new TextRun({ text: '[LOGO] ', font: F, size: 14, bold: true, color: P }),
                      new TextRun({ text: firm.toLowerCase(), font: F, size: 16, color: P })
                    ]
                  })]
                })
              ]})]
            })
          ]
        })
      },
      footers: {
        default: new Footer({
          children: [new Paragraph({
            tabStops: [{ type: 'right', position: 9360 }],
            children: [
              new TextRun({ text: `${clientName} Executive Advisory Report`, font: F, size: 16, color: '64748B' }),
              new TextRun({ text: '\t| ', font: F, size: 16, color: '64748B' }),
              new TextRun({ children: [PageNumber.CURRENT], font: F, size: 16, color: '64748B' })
            ]
          })]
        })
      },
      children: [
        ...coverBlock,
        pageBreak(),
        ...tocBlock,
        pageBreak(),
        ...execSummaryBlock
      ]
    }]
  })

  const blob = await Packer.toBlob(doc)
  const safeName = (clientName || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  saveAs(blob, `${safeName}-executive-advisory-report.docx`)
}

// ============================================================
// Block builders
// ============================================================

function buildCover({ txt, firm, clientName, dateStr, P, A, b }) {
  const blocks = []

  // Header band with logo placeholder slot
  blocks.push(new Table({
    width: { size: CW, type: WidthType.DXA },
    columnWidths: [Math.floor(CW * 0.25), Math.floor(CW * 0.75)],
    rows: [new TableRow({ cantSplit: true, children: [
      new TableCell({
        width: { size: Math.floor(CW * 0.25), type: WidthType.DXA },
        shading: { fill: P, type: ShadingType.CLEAR },
        borders: noBd(),
        margins: { top: 240, bottom: 240, left: 240, right: 240 },
        verticalAlign: 'center',
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: '[LOGO PLACEHOLDER]', font: b.font, size: 18, bold: true, color: A })]
          })
        ]
      }),
      new TableCell({
        width: { size: Math.floor(CW * 0.75), type: WidthType.DXA },
        shading: { fill: P, type: ShadingType.CLEAR },
        borders: noBd(),
        margins: { top: 240, bottom: 240, left: 280, right: 280 },
        children: [
          new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: firm.toUpperCase(), font: b.font, size: 20, bold: true, color: A })] }),
          new Paragraph({ children: [new TextRun({ text: 'EXECUTIVE ADVISORY REPORT', font: b.font, size: 16, color: 'FFFFFF' })] })
        ]
      })
    ]})]
  }))
  blocks.push(gap(400))

  blocks.push(new Paragraph({
    children: [new TextRun({ text: 'Executive Advisory Report', font: b.font, size: 56, bold: true, color: P })],
    spacing: { after: 200 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: b.firmLegalName || 'Sidewalk Security Advisors LLC', font: b.font, size: 24, bold: true, color: '2D3748' })],
    spacing: { after: 80 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: dateStr.toUpperCase(), font: b.font, size: 22, italics: true, color: P, highlight: 'yellow' })],
    spacing: { after: 400 }
  }))

  blocks.push(new Paragraph({
    children: [new TextRun({ text: 'Prepared for:', font: b.font, size: 22, bold: true, color: '2D3748' })],
    spacing: { after: 60 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: clientName, font: b.font, size: 22, color: '2D3748' })],
    spacing: { after: 40 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: clientName, font: b.font, size: 20, italics: true, color: '64748B' })],
    spacing: { after: 300 }
  }))

  blocks.push(new Paragraph({
    children: [new TextRun({ text: 'Created by:', font: b.font, size: 22, bold: true, color: '2D3748' })],
    spacing: { after: 60 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: `${b.authorName || 'Jeremy Krienke'} – ${b.authorTitle || 'Principal Advisor'}`, font: b.font, size: 22, color: '2D3748' })],
    spacing: { after: 40 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: b.firmLegalName || 'Sidewalk Security Advisors LLC', font: b.font, size: 20, italics: true, color: '64748B' })]
  }))

  return blocks
}

function buildToc({ txt, P, A, draft }) {
  const blocks = []
  blocks.push(new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 0, after: 200 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: A, space: 4 } },
    children: [new TextRun({ text: 'Table of Contents', bold: true, size: 32, color: P })]
  }))

  const sections = [
    { num: '01', title: 'Executive Summary', status: draft ? 'INCLUDED' : 'placeholder' },
    { num: '02', title: 'Business Goals & Engagement Context', status: 'placeholder' },
    { num: '03', title: 'Top Threats & Breach Analysis', status: 'placeholder' },
    { num: '04', title: 'Engagement Findings', status: 'placeholder' },
    { num: '05', title: 'Compliance & Maturity Coverage', status: 'placeholder' },
    { num: '06', title: 'Key Strengths', status: 'placeholder' },
    { num: '07', title: 'Key Risks & Weaknesses', status: 'placeholder' },
    { num: '08', title: 'Strategic Opportunities', status: 'placeholder' },
    { num: '09', title: 'Cyber Liability Insurance Review', status: 'placeholder' },
    { num: '10', title: 'Strategic Roadmap Overview', status: 'placeholder' },
    { num: '11', title: 'Next Steps for Leadership', status: 'placeholder' },
    { num: 'A', title: 'Appendix A — Acronyms', status: 'placeholder' },
    { num: 'B', title: 'Appendix B — Document Review List', status: 'placeholder' },
    { num: 'C', title: 'Appendix C — Detailed Threat Profile', status: 'placeholder' },
    { num: 'D', title: 'Appendix D — Risk Analysis', status: 'placeholder' }
  ]

  sections.forEach(s => {
    blocks.push(new Paragraph({
      spacing: { before: 60, after: 60 },
      tabStops: [{ type: 'right', position: 9360 }],
      children: [
        new TextRun({ text: s.num, bold: true, color: P, size: 22 }),
        new TextRun({ text: '   ' + s.title, color: '2D3748', size: 22 }),
        new TextRun({ text: '\t' }),
        s.status === 'INCLUDED'
          ? new TextRun({ text: 'INCLUDED', bold: true, color: '228B22', size: 18 })
          : new TextRun({ text: '[placeholder]', italics: true, color: '94A3B8', size: 18 })
      ]
    }))
  })

  blocks.push(gap(240))
  blocks.push(calloutParagraph(P, 'About this report', 'This document reflects the current engagement state. Sections marked [placeholder] will be populated as underlying data is gathered and approved. Sections marked LOW CONFIDENCE flag areas requiring advisor review before delivery.'))

  return blocks
}

function buildExecutiveSummary({ txt, P, A, draft, review, context }) {
  const blocks = []
  const confidence = review?.sectionConfidence || {}

  blocks.push(sectionHeader('Executive Summary', P, A))

  // Headline
  if (draft?.headline && draft.headline !== 'DATA_NOT_PROVIDED') {
    blocks.push(new Paragraph({
      spacing: { after: SP6 },
      children: [new TextRun({ text: draft.headline, bold: true, size: 28, color: P })]
    }))
    maybeConfidence(blocks, confidence.headline, 'Headline')
  } else {
    blocks.push(placeholderBlock('Headline not generated', 'Run assessment to generate executive headline.'))
  }

  // Program Snapshot
  blocks.push(subHeader('Information Security Program Snapshot', P))
  const score = Math.round(context?.scores?.overall?.score || 0)
  const level = context?.scores?.overall?.maturityLabel || 'Initial'
  blocks.push(new Paragraph({
    spacing: { after: 80 },
    children: [
      new TextRun({ text: 'Overall Maturity: ', bold: true, color: P, size: 22 }),
      new TextRun({ text: `${score}% `, bold: true, color: scoreHex(score), size: 28 }),
      new TextRun({ text: `(${level})`, italics: true, color: '64748B', size: 22 })
    ]
  }))
  if (draft?.programSnapshotNarrative && draft.programSnapshotNarrative !== 'DATA_NOT_PROVIDED') {
    blocks.push(bodyParagraph(draft.programSnapshotNarrative))
    maybeConfidence(blocks, confidence.programSnapshotNarrative, 'Snapshot narrative')
  } else {
    blocks.push(placeholderBlock('Program snapshot narrative not generated', 'Narrative requires computed scores.'))
  }

  // Top Risks
  blocks.push(subHeader('Top 3 Risks', P))
  if (Array.isArray(draft?.topRisks) && draft.topRisks.length > 0) {
    draft.topRisks.forEach((risk, idx) => {
      blocks.push(new Paragraph({
        spacing: { before: 100, after: 40 },
        children: [new TextRun({ text: `${idx + 1}. ${risk?.title || 'Untitled risk'}`, bold: true, color: P, size: 22 })]
      }))
      if (risk?.rationale) blocks.push(bodyParagraph(risk.rationale, { indent: { left: 300 } }))
      if (risk?.businessImpact) {
        blocks.push(impactCallout(risk.businessImpact))
      }
    })
    maybeConfidence(blocks, confidence.topRisks, 'Top Risks')
  } else {
    blocks.push(placeholderBlock('Risks not generated', 'No top risks returned from analysis.'))
  }

  // Top Opportunities
  blocks.push(subHeader('Top 3 Opportunities', P))
  if (Array.isArray(draft?.topOpportunities) && draft.topOpportunities.length > 0) {
    draft.topOpportunities.forEach((opp, idx) => {
      blocks.push(new Paragraph({
        spacing: { before: 100, after: 40 },
        children: [new TextRun({ text: `${idx + 1}. ${opp?.title || 'Untitled opportunity'}`, bold: true, color: A, size: 22 })]
      }))
      if (opp?.rationale) blocks.push(bodyParagraph(opp.rationale, { indent: { left: 300 } }))
      if (opp?.value) {
        blocks.push(new Paragraph({
          spacing: { after: 60 },
          indent: { left: 300 },
          children: [new TextRun({ text: `Value: ${opp.value}`, bold: true, color: '228B22', size: 20 })]
        }))
      }
    })
    maybeConfidence(blocks, confidence.topOpportunities, 'Top Opportunities')
  } else {
    blocks.push(placeholderBlock('Opportunities not generated', 'No top opportunities returned from analysis.'))
  }

  // Regulatory Alignment
  blocks.push(subHeader('Regulatory Alignment', P))
  if (draft?.regulatoryAlignment?.narrative && draft.regulatoryAlignment.narrative !== 'DATA_NOT_PROVIDED') {
    blocks.push(bodyParagraph(draft.regulatoryAlignment.narrative))
    if (draft.regulatoryAlignment.coverageNotes && draft.regulatoryAlignment.coverageNotes !== 'DATA_NOT_PROVIDED') {
      blocks.push(bodyParagraph(draft.regulatoryAlignment.coverageNotes, { italics: true }))
    }
  } else {
    blocks.push(placeholderBlock('Regulatory alignment not generated', 'Run assessment with industry and framework data.'))
  }
  if (Array.isArray(context?.applicableFrameworks) && context.applicableFrameworks.length > 0) {
    blocks.push(new Paragraph({
      spacing: { before: 80 },
      children: [
        new TextRun({ text: 'Applicable Frameworks: ', bold: true, size: 20, color: P }),
        new TextRun({ text: context.applicableFrameworks.filter(Boolean).map(f => typeof f === 'string' ? f : (f.name || f.id)).join(' • '), size: 20, color: '2D3748' })
      ]
    }))
  }
  maybeConfidence(blocks, confidence.regulatoryAlignment, 'Regulatory Alignment')

  // Business Goals
  blocks.push(subHeader('Business Goals (Inferred)', P))
  blocks.push(new Paragraph({
    spacing: { after: 80 },
    shading: { fill: 'FEF3C7', type: ShadingType.CLEAR },
    children: [new TextRun({ text: 'Draft — requires admin confirmation before client delivery.', italics: true, color: '92400E', size: 18 })]
  }))
  if (Array.isArray(draft?.businessGoals) && draft.businessGoals.length > 0) {
    draft.businessGoals.forEach(g => {
      const confColor = g.confidence === 'high' ? '228B22' : g.confidence === 'medium' ? 'EA580C' : 'CA8A04'
      blocks.push(new Paragraph({
        spacing: { before: 80, after: 20 },
        children: [
          new TextRun({ text: '• ', bold: true, color: P, size: 22 }),
          new TextRun({ text: g.goal || 'Unnamed goal', bold: true, color: '2D3748', size: 22 }),
          new TextRun({ text: '   ' }),
          g.confidence ? new TextRun({ text: `[${String(g.confidence).toUpperCase()}]`, bold: true, color: confColor, size: 16 }) : new TextRun({ text: '' })
        ]
      }))
      if (g.evidence) {
        blocks.push(new Paragraph({
          spacing: { after: 40 },
          indent: { left: 300 },
          children: [new TextRun({ text: `Evidence: ${g.evidence}`, italics: true, color: '94A3B8', size: 18 })]
        }))
      }
    })
    maybeConfidence(blocks, confidence.businessGoals, 'Business Goals')
  } else {
    blocks.push(placeholderBlock('Business goals not inferred', 'Insufficient data to infer business goals.'))
  }

  // QA Review callout
  if (review?.qualityNotes) {
    blocks.push(gap(200))
    const tone = review.overallConfidence != null && review.overallConfidence < 70 ? 'warning' : 'info'
    blocks.push(calloutParagraph(
      tone === 'warning' ? 'EA580C' : P,
      `QA Review — Overall Confidence: ${review.overallConfidence ?? 'N/A'}${typeof review.overallConfidence === 'number' ? '%' : ''}`,
      review.qualityNotes
    ))
  }

  return blocks
}

// ============================================================
// Primitives
// ============================================================

function sectionHeader(title, P, A) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 0, after: 200 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: A, space: 4 } },
    children: [new TextRun({ text: title, bold: true, size: 36, color: P })]
  })
}

function subHeader(title, P) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: 120 },
    children: [new TextRun({ text: title, bold: true, size: 24, color: P })]
  })
}

function bodyParagraph(text, opts = {}) {
  return new Paragraph({
    spacing: { before: 40, after: 80 },
    indent: opts.indent,
    children: [new TextRun({ text: String(text || ''), italics: !!opts.italics, color: '475569', size: 22 })]
  })
}

function placeholderBlock(title, body) {
  return new Paragraph({
    spacing: { before: 80, after: 80 },
    shading: { fill: 'F1F5F9', type: ShadingType.CLEAR },
    border: {
      top: { style: BorderStyle.DASHED, size: 2, color: '94A3B8' },
      bottom: { style: BorderStyle.DASHED, size: 2, color: '94A3B8' },
      left: { style: BorderStyle.DASHED, size: 2, color: '94A3B8' },
      right: { style: BorderStyle.DASHED, size: 2, color: '94A3B8' }
    },
    children: [
      new TextRun({ text: `[PLACEHOLDER] ${title}`, bold: true, color: '94A3B8', size: 18 }),
      new TextRun({ text: ` — ${body}`, italics: true, color: '94A3B8', size: 18 })
    ]
  })
}

function impactCallout(text) {
  return new Paragraph({
    spacing: { before: 40, after: 120 },
    indent: { left: 300 },
    shading: { fill: 'FEE2E2', type: ShadingType.CLEAR },
    children: [
      new TextRun({ text: 'Business Impact: ', bold: true, color: '991B1B', size: 20 }),
      new TextRun({ text, italics: true, color: '991B1B', size: 20 })
    ]
  })
}

function calloutParagraph(barColorHex, title, body) {
  return new Paragraph({
    spacing: { before: 120, after: 120 },
    shading: { fill: 'F1F5F9', type: ShadingType.CLEAR },
    border: { left: { style: BorderStyle.SINGLE, size: 12, color: barColorHex, space: 8 } },
    children: [
      new TextRun({ text: title, bold: true, color: barColorHex, size: 20 }),
      new TextRun({ text: '\n' }),
      new TextRun({ text: body, color: '475569', size: 20, break: 1 })
    ]
  })
}

function maybeConfidence(blocks, value, label) {
  if (typeof value !== 'number' || value >= 70) return
  blocks.push(new Paragraph({
    spacing: { after: 80 },
    shading: { fill: 'FEF3C7', type: ShadingType.CLEAR },
    children: [new TextRun({
      text: `⚠ LOW CONFIDENCE (${value}%) — ${label}: flagged for advisor review`,
      bold: true, color: '92400E', size: 16
    })]
  }))
}

function gap(b = 120) {
  return new Paragraph({ spacing: { before: b, after: 0 }, children: [] })
}

function pageBreak() {
  return new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true })
}

function noBd() {
  const s = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
  return { top: s, bottom: s, left: s, right: s }
}

function strip(c) { return (c || '').replace(/^#/, '') }

function scoreHex(score) {
  if (score >= 80) return '22C55E'
  if (score >= 60) return '3B82F6'
  if (score >= 40) return 'F59E0B'
  if (score >= 20) return 'F97316'
  return 'EF4444'
}
